import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { NIGERIAN_STATES } from "@client/lib/nigerian-states"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { Select } from "./components/select"
import { Portal } from "./components/portal"
import { SponsorMatchModal } from "./components/sponsor-match-modal"
import { SplitActionButton } from "./components/split-action-button"
import { ActivityTimeline } from "./components/activity-timeline"
import { DetailPageSkeleton } from "./components/skeleton"
import { AdminActivityAction, AdminActivityEntityType } from "@shared/admin-activities"

type SponsorStatus = "active" | "paused" | "lapsed"
type SponsorTier = "sustainer" | "founder" | "friend"
type GivingSchedule = "monthly" | "termly" | "yearly"
type SponsorshipRequestStatus = "new" | "contacted" | "approved" | "declined" | "closed"

interface Sponsor {
  id: string
  code: string
  name: string
  email: string
  phone: string | null
  city: string | null
  givingSchedule: GivingSchedule
  emailUpdates: boolean
  physicalLetters: boolean
  publicName: boolean
  tier: SponsorTier
  status: SponsorStatus
  since: number
  lifetimeDonated: number
  lastGiftAt: string | null
  nextExpectedAt: string | null
  monthlyCommitment: number | null
  internalNotes: string | null
  communicationLog: { event: string; date: string }[] | null
  childrenCount: number
  pausedAt: string | null
  pauseReason: string | null
  createdAt: string
  updatedAt: string
  sponsorshipRequest: SponsorshipRequest | null
}

interface SponsorshipRequest {
  id: string
  status: SponsorshipRequestStatus
  message: string
  createdAt: string
  updatedAt: string
}

interface SponsoredChild {
  id: string
  code: string
  firstName: string
  lastName: string
  age: number
  state: string
  photoUrl: string | null
  sponsoredAt: string | null
}

interface AvailableChild {
  id: string
  code: string
  firstName: string
  lastName: string
  age: number
  gender: string
  state: string
  photoUrl: string | null
}

const STATUS_COLORS: Record<SponsorStatus | SponsorshipRequestStatus, { bg: string; text: string }> = {
  active: { bg: "bg-green-100", text: "text-green-700" },
  paused: { bg: "bg-gold-100", text: "text-gold-700" },
  lapsed: { bg: "bg-red-100", text: "text-red-700" },
  new: { bg: "bg-gold-100", text: "text-gold-700" },
  contacted: { bg: "bg-blue-100", text: "text-blue-700" },
  approved: { bg: "bg-green-100", text: "text-green-700" },
  declined: { bg: "bg-red-100", text: "text-red-700" },
  closed: { bg: "bg-ink/10", text: "text-ink/70" },
}

const STATUS_LABELS: Record<SponsorStatus | SponsorshipRequestStatus, string> = {
  active: "Active",
  paused: "Paused",
  lapsed: "Lapsed",
  new: "Submitted",
  contacted: "Reviewing",
  approved: "Approved",
  declined: "Declined",
  closed: "Closed",
}

const TIER_LABELS: Record<SponsorTier, string> = {
  sustainer: "Sustainer",
  founder: "Founder",
  friend: "Friend",
}

const SPONSOR_FILTER_STATES = ["All States", ...NIGERIAN_STATES] as const

const MESSAGE_TEMPLATES = ["Custom", "Term update", "Payment reminder", "Thank you"] as const
const MESSAGE_TEMPLATE_CONTENT: Record<typeof MESSAGE_TEMPLATES[number], { subject: string; message: string }> = {
  Custom: { subject: "", message: "" },
  "Term update": {
    subject: "A term update from Redempta",
    message: "Hi {name},\n\nHere is a quick update on the current school term. We will share more details as they become available.",
  },
  "Payment reminder": {
    subject: "A reminder from Redempta",
    message: "Hi {name},\n\nThis is a friendly reminder about your sponsorship commitment. Please let us know if you have any questions.",
  },
  "Thank you": {
    subject: "Thank you for supporting Redempta",
    message: "Hi {name},\n\nThank you for your continued support. Your generosity is helping create access to education and opportunity.",
  },
}

function formatCurrency(amountInKobo: number): string {
  const naira = amountInKobo / 100
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(naira)
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "—"
  const date = new Date(dateString)
  return date.toLocaleDateString("en-NG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function formatGivingSchedule(schedule: GivingSchedule): string {
  return schedule.charAt(0).toUpperCase() + schedule.slice(1)
}

function formatActivityDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "today"
  if (diffDays === 1) return "yesterday"
  if (diffDays < 7) return `${diffDays} days ago`
  return formatDate(dateString)
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function MessageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  )
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  )
}

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="4" width="4" height="16" />
      <rect x="14" y="4" width="4" height="16" />
    </svg>
  )
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  )
}

export function SponsorDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const canView = useHasPermission("sponsors")
  const canMutate = useHasPermission("sponsors_mutate")
  const [sponsor, setSponsor] = useState<Sponsor | null>(null)
  const [sponsoredChildren, setSponsoredChildren] = useState<SponsoredChild[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [showMatchModal, setShowMatchModal] = useState(false)
  const [showGlobalMatchModal, setShowGlobalMatchModal] = useState(false)
  const [showUnmatchModal, setShowUnmatchModal] = useState(false)
  const [showPauseModal, setShowPauseModal] = useState(false)
  const [activityRefreshKey, setActivityRefreshKey] = useState(0)
  const modalRef = useRef<HTMLDivElement>(null)

  // Match modal state
  const [availableChildren, setAvailableChildren] = useState<AvailableChild[]>([])
  const [selectedChild, setSelectedChild] = useState<AvailableChild | null>(null)
  const [childToUnmatch, setChildToUnmatch] = useState<SponsoredChild | null>(null)
  const [unmatchReason, setUnmatchReason] = useState("")
  const [matchSearch, setMatchSearch] = useState("")
  const [matchState, setMatchState] = useState("All States")
  const [matchGender, setMatchGender] = useState("All")
  const [loadingChildren, setLoadingChildren] = useState(false)
  const [matchStep, setMatchStep] = useState<1 | 2 | 3>(1)

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    phone: "",
    tier: "sustainer" as SponsorTier,
    monthlyCommitment: "",
    internalNotes: "",
  })

  // Message form state
  const [messageForm, setMessageForm] = useState({
    template: "Custom",
    subject: "",
    message: "",
  })

  // Pause form state
  const [pauseReason, setPauseReason] = useState("")

  const fetchSponsor = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.sponsors[":id"].$get({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        const s = body.data as Sponsor
        setSponsor(s)
        setEditForm({
          name: s.name,
          email: s.email,
          phone: s.phone ?? "",
          tier: s.tier,
          monthlyCommitment: s.monthlyCommitment ? String(s.monthlyCommitment / 100) : "",
          internalNotes: s.internalNotes ?? "",
        })
      }
    } catch {
      setError("Failed to load sponsor")
    } finally {
      setLoading(false)
    }
  }

  const fetchSponsoredChildren = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.sponsors[":id"].children.$get({ param: { id } })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setSponsoredChildren(body.data.items as SponsoredChild[])
      }
    } catch {
      // Silently fail - non-critical
    }
  }

  const fetchAvailableChildren = async () => {
    if (!id) return
    setLoadingChildren(true)
    try {
      const query: { search?: string; state?: string; gender?: "male" | "female" } = {}
      if (matchSearch.trim()) query.search = matchSearch.trim()
      if (matchState !== "All States") query.state = matchState
      if (matchGender !== "All") query.gender = matchGender.toLowerCase() as "male" | "female"

      const res = await client.api.admin.sponsors[":id"]["available-children"].$get({
        param: { id },
        query,
      })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setAvailableChildren(body.data.items as AvailableChild[])
      }
    } catch {
      toast.error("Failed to load available children")
    } finally {
      setLoadingChildren(false)
    }
  }

  const reloadPageData = async () => {
    await Promise.all([fetchSponsor(), fetchSponsoredChildren()])
    setActivityRefreshKey((key) => key + 1)
  }

  useEffect(() => {
    if (canView) {
      void fetchSponsor()
      void fetchSponsoredChildren()
    } else {
      setLoading(false)
    }
  }, [id, canView])

  useEffect(() => {
    if (showMatchModal) {
      void fetchAvailableChildren()
    }
  }, [showMatchModal, matchSearch, matchState, matchGender])

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !canMutate) return
    setSaving(true)
    try {
      const res = await client.api.admin.sponsors[":id"].$patch({
        param: { id },
        json: {
          name: editForm.name,
          email: editForm.email,
          phone: editForm.phone || undefined,
          tier: editForm.tier,
          monthlyCommitment: editForm.monthlyCommitment ? Number(editForm.monthlyCommitment) * 100 : undefined,
          internalNotes: editForm.internalNotes || undefined,
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Sponsor updated successfully")
      setShowEditModal(false)
      await reloadPageData()
    } catch {
      toast.error("Failed to update sponsor")
    } finally {
      setSaving(false)
    }
  }

  const handlePause = async () => {
    if (!id || !canMutate) return
    setSaving(true)
    try {
      const res = await client.api.admin.sponsors[":id"].pause.$post({
        param: { id },
        json: { reason: pauseReason || undefined },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Sponsor paused")
      setShowPauseModal(false)
      setPauseReason("")
      await reloadPageData()
    } catch {
      toast.error("Failed to pause sponsor")
    } finally {
      setSaving(false)
    }
  }

  const handleResume = async () => {
    if (!id || !canMutate) return
    setSaving(true)
    try {
      const res = await client.api.admin.sponsors[":id"].resume.$post({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Sponsor resumed")
      await reloadPageData()
    } catch {
      toast.error("Failed to resume sponsor")
    } finally {
      setSaving(false)
    }
  }

  const handleMatchChild = async () => {
    if (!id || !selectedChild || !canMutate) return
    setSaving(true)
    try {
      const res = await client.api.admin.sponsors[":id"].match.$post({
        param: { id },
        json: { childId: selectedChild.id },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success(`Matched ${selectedChild.firstName} ${selectedChild.lastName} with ${sponsor?.name}`)
      setShowMatchModal(false)
      setSelectedChild(null)
      setMatchStep(1)
      await reloadPageData()
    } catch {
      toast.error("Failed to match child")
    } finally {
      setSaving(false)
    }
  }

  const handleUnmatchChild = async () => {
    if (!id || !childToUnmatch || !unmatchReason.trim() || !canMutate) return
    setSaving(true)
    try {
      const res = await client.api.admin.sponsors[":id"].unmatch.$post({
        param: { id },
        json: { childId: childToUnmatch.id, reason: unmatchReason.trim() },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success(`Ended the match for ${childToUnmatch.firstName} ${childToUnmatch.lastName}`)
      setShowUnmatchModal(false)
      setChildToUnmatch(null)
      setUnmatchReason("")
      await reloadPageData()
    } catch {
      toast.error("Failed to end child match")
    } finally {
      setSaving(false)
    }
  }

  const handleSponsorshipStatusChange = async (status: SponsorshipRequestStatus) => {
    const request = sponsor?.sponsorshipRequest
    if (!request || request.status === status || !canMutate) return
    setSaving(true)
    try {
      const res = await client.api.admin.data.inquiries[":id"].$patch({
        param: { id: request.id },
        json: { status },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success(`Sponsorship request updated to ${status}`)
      await reloadPageData()
    } catch {
      toast.error("Failed to update sponsorship request")
    } finally {
      setSaving(false)
    }
  }

  const handleMessageSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!id || !canMutate || !messageForm.subject.trim() || !messageForm.message.trim()) return

    setSaving(true)
    try {
      const res = await client.api.admin.sponsors[":id"].message.$post({
        param: { id },
        json: {
          subject: messageForm.subject.trim(),
          message: messageForm.message.trim(),
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Message sent to sponsor")
      setShowMessageModal(false)
      setMessageForm({ template: "Custom", subject: "", message: "" })
      await reloadPageData()
    } catch {
      toast.error("Failed to send message")
    } finally {
      setSaving(false)
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setShowEditModal(false)
      setShowMessageModal(false)
      setShowMatchModal(false)
      setShowUnmatchModal(false)
      setShowPauseModal(false)
      setSelectedChild(null)
      setChildToUnmatch(null)
      setMatchStep(1)
    }
  }

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Sponsor</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view this sponsor.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <DetailPageSkeleton />
  }

  if (error || !sponsor) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/admin/sponsors")}
          className="inline-flex items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <BackIcon className="h-4 w-4" />
          Back
        </button>
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error || "Sponsor not found"}</p>
        </div>
      </div>
    )
  }

  const displayStatus = sponsor.sponsorshipRequest?.status ?? sponsor.status
  const canMatch = sponsor.status === "active" && sponsor.sponsorshipRequest?.status === "approved"

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <button
          onClick={() => navigate("/admin/sponsors")}
          className="inline-flex w-fit items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <BackIcon className="h-4 w-4" />
          Back
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-forest-950 text-lg font-bold text-white">
              {sponsor.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
            </span>
            <div>
              <p className="text-xs uppercase tracking-wider text-ink/50">Sponsor · {sponsor.code}</p>
              <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">{sponsor.name}</h1>
              <p className="mt-1 text-sm text-ink/60">
                {TIER_LABELS[sponsor.tier]} · Since {sponsor.since} · {sponsor.childrenCount} {sponsor.childrenCount === 1 ? "child" : "children"}
              </p>
              <span
                className={`mt-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                   STATUS_COLORS[displayStatus].bg
                 } ${STATUS_COLORS[displayStatus].text}`}
               >
                 {STATUS_LABELS[displayStatus]}
               </span>
            </div>
          </div>

          {canMutate && (
            <SplitActionButton
              label="Message"
              icon={<MessageIcon className="h-4 w-4" />}
              onClick={() => setShowMessageModal(true)}
              disabled={saving}
              items={[
                ...(sponsor.status === "active"
                  ? [{ label: "Pause", icon: <PauseIcon className="h-4 w-4" />, onClick: () => setShowPauseModal(true) }]
                  : sponsor.status === "paused"
                    ? [{ label: "Resume", icon: <PlayIcon className="h-4 w-4" />, onClick: () => void handleResume() }]
                    : []),
                {
                  label: "Edit",
                  icon: <EditIcon className="h-4 w-4" />,
                  onClick: () => setShowEditModal(true),
                },
              ]}
            />
          )}
        </div>
      </div>

      {/* Sponsor Profile */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Sponsor Profile</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <p className="text-xs text-ink/50">Email</p>
            <p className="text-sm font-medium text-ink">{sponsor.email}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Phone</p>
            <p className="text-sm font-medium text-ink">{sponsor.phone || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">City</p>
            <p className="text-sm font-medium text-ink">{sponsor.city || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Tier</p>
            <p className="text-sm font-medium text-ink">{TIER_LABELS[sponsor.tier]}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Sponsor Since</p>
            <p className="text-sm font-medium text-ink">{sponsor.since}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Sponsor Code</p>
            <p className="text-sm font-medium text-ink">{sponsor.code}</p>
          </div>
          <div>
             <p className="text-xs text-ink/50">Account status</p>
             <p className="text-sm font-medium text-ink capitalize">{sponsor.status}</p>
          </div>
        </div>
      </div>

      {/* Giving */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Giving</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <p className="text-xs text-ink/50">Monthly Commitment</p>
            <p className="text-sm font-medium text-ink">
              {sponsor.monthlyCommitment ? formatCurrency(sponsor.monthlyCommitment) : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Lifetime Donated</p>
            <p className="text-sm font-medium text-ink">{formatCurrency(sponsor.lifetimeDonated)}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Last Gift</p>
            <p className="text-sm font-medium text-ink">{formatDate(sponsor.lastGiftAt)}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Next Expected</p>
            <p className="text-sm font-medium text-ink">{formatDate(sponsor.nextExpectedAt)}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Giving Schedule</p>
            <p className="text-sm font-medium text-ink">{formatGivingSchedule(sponsor.givingSchedule)}</p>
          </div>
        </div>
      </div>

      {/* Communication Preferences */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Communication Preferences</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <p className="text-xs text-ink/50">Term updates and report cards</p>
            <p className="text-sm font-medium text-ink">{sponsor.emailUpdates ? "Yes" : "No"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Physical letters</p>
            <p className="text-sm font-medium text-ink">{sponsor.physicalLetters ? "Yes" : "No"}</p>
          </div>
          <div>
            <p className="text-xs text-ink/50">Public supporter name</p>
            <p className="text-sm font-medium text-ink">{sponsor.publicName ? "Yes" : "No"}</p>
          </div>
        </div>
      </div>

      {/* Internal Notes */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Internal Notes</h2>
        <div className="mt-4 rounded-lg bg-cream-100 p-3">
          <p className="text-sm text-ink">{sponsor.internalNotes || "No internal notes."}</p>
        </div>
      </div>

      {/* Sponsored Children */}
      {sponsor.sponsorshipRequest && (
        <div className="rounded-[10px] border border-gold-500 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Sponsorship Request</h2>
              <p className="mt-2 text-sm text-ink/60">Received {formatDate(sponsor.sponsorshipRequest.createdAt)} · Updated {formatDate(sponsor.sponsorshipRequest.updatedAt)}</p>
            </div>
            <span className="rounded-full bg-cream-100 px-3 py-1 text-sm font-medium capitalize text-ink">
              {sponsor.sponsorshipRequest.status === "new" ? "Submitted" : sponsor.sponsorshipRequest.status === "contacted" ? "Reviewing" : sponsor.sponsorshipRequest.status}
            </span>
          </div>
          <p className="mt-4 whitespace-pre-wrap rounded-lg bg-cream-100 p-4 text-sm leading-6 text-ink/75">{sponsor.sponsorshipRequest.message}</p>
          {canMutate && (
            <div className="mt-4 flex flex-wrap gap-2">
              {(["new", "contacted", "approved", "declined", "closed"] as const).map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => void handleSponsorshipStatusChange(status)}
                  disabled={saving || sponsor.sponsorshipRequest?.status === status}
                  className={`rounded-full border px-4 py-2 text-sm font-medium capitalize transition-colors disabled:opacity-50 ${sponsor.sponsorshipRequest?.status === status ? "border-forest-700 bg-forest-700 text-white" : "border-ink/20 text-ink hover:bg-cream-100"}`}
                >
                  {status === "new" ? "Submitted" : status === "contacted" ? "Reviewing" : status}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="rounded-[10px] border border-gold-500 bg-white p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Sponsored Children</h2>
           {canMutate && canMatch && (
             <button
              onClick={() => setShowGlobalMatchModal(true)}
              className="inline-flex items-center gap-1 text-sm font-medium text-forest-700 hover:underline"
            >
              <PlusIcon className="h-4 w-4" />
              Match another
            </button>
          )}
        </div>
        {sponsoredChildren.length > 0 ? (
          <div className="mt-4 space-y-3">
            {sponsoredChildren.map((child) => (
              <div
                key={child.id}
                onClick={() => navigate(`/admin/children/${child.id}`)}
                className="flex cursor-pointer items-center justify-between rounded-lg border border-gold-500/30 p-3 transition-colors hover:bg-cream-50"
              >
                <div className="flex items-center gap-3">
                  {child.photoUrl ? (
                    <img
                      src={child.photoUrl}
                      alt={`${child.firstName} ${child.lastName}`}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest-950 text-xs font-bold text-white">
                      {child.firstName[0]}{child.lastName[0]}
                    </span>
                  )}
                  <div>
                    <p className="text-sm font-medium text-ink">{child.firstName} {child.lastName}</p>
                    <p className="text-xs text-ink/50">
                      Age {child.age} · {child.state} · {child.code}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {canMutate && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        setChildToUnmatch(child)
                        setUnmatchReason("")
                        setShowUnmatchModal(true)
                      }}
                      className="rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                    >
                      End match
                    </button>
                  )}
                  <ExternalLinkIcon className="h-4 w-4 text-ink/40" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4 rounded-lg bg-cream-100 p-4 text-center">
            <p className="text-sm text-ink/70">No children sponsored yet.</p>
            {canMutate && canMatch && (
              <button
                onClick={() => setShowGlobalMatchModal(true)}
                className="mt-2 inline-flex items-center gap-2 rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-forest-900"
              >
                <PlusIcon className="h-4 w-4" />
                Match a child
              </button>
            )}
          </div>
        )}
      </div>

      <ActivityTimeline
        entityType={AdminActivityEntityType.Sponsor}
        entityId={sponsor.id}
        title="Communication"
        action={AdminActivityAction.SponsorMessageSent}
        refreshKey={activityRefreshKey}
        fallback={
          <div className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Communication</h2>
            {sponsor.communicationLog && sponsor.communicationLog.length > 0 ? (
              <div className="mt-4 space-y-3">
                {sponsor.communicationLog.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-forest-700" />
                    <div>
                      <span className="text-sm font-medium text-ink">{item.event}</span>
                      <span className="text-sm text-ink/50"> · {formatActivityDate(item.date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-sm text-ink/50">No communication recorded.</p>
            )}
          </div>
        }
      />

      <ActivityTimeline
        entityType={AdminActivityEntityType.Sponsor}
        entityId={sponsor.id}
        title="Activity"
        excludeActions={[AdminActivityAction.SponsorMessageSent]}
        hideWhenEmpty
        refreshKey={activityRefreshKey}
      />

      {/* Edit Modal */}
      {showEditModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Edit Sponsor</h2>
            <p className="mt-1 text-sm text-ink/60">{sponsor.code} · {sponsor.name}</p>
            <form onSubmit={handleEditSubmit} className="mt-6 space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Name *</span>
                <input
                  type="text"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Email *</span>
                <input
                  type="email"
                  required
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Phone</span>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>

              <Select
                label="Tier"
                value={TIER_LABELS[editForm.tier]}
                onChange={(value) => {
                  const tier = Object.entries(TIER_LABELS).find(([, label]) => label === value)?.[0] as SponsorTier
                  setEditForm({ ...editForm, tier })
                }}
                options={Object.values(TIER_LABELS)}
              />

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Monthly Commitment (NGN)</span>
                <input
                  type="number"
                  value={editForm.monthlyCommitment}
                  onChange={(e) => setEditForm({ ...editForm, monthlyCommitment: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="50000"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Internal Notes</span>
                <textarea
                  value={editForm.internalNotes}
                  onChange={(e) => setEditForm({ ...editForm, internalNotes: e.target.value })}
                  rows={3}
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Internal notes about this sponsor..."
                />
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
          </div>
        </Portal>
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Message Sponsor</h2>
            <p className="mt-1 text-sm text-ink/60">{sponsor.code} · {sponsor.name}</p>
            <form className="mt-6 space-y-4" onSubmit={handleMessageSubmit}>
              <Select
                label="Template"
                value={messageForm.template}
                onChange={(value) => {
                  const template = value as typeof MESSAGE_TEMPLATES[number]
                  const content = MESSAGE_TEMPLATE_CONTENT[template]
                  setMessageForm({
                    template,
                    subject: content.subject,
                    message: content.message.replace("{name}", sponsor.name.split(" ")[0] ?? sponsor.name),
                  })
                }}
                options={MESSAGE_TEMPLATES}
              />

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Subject</span>
                <input
                  type="text"
                  required
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Your message subject"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Message</span>
                <textarea
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  rows={5}
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder={`Hi ${sponsor.name.split(" ")[0]},\n\nThank you for your continued support...`}
                />
              </label>

              <div className="flex justify-start gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving || !messageForm.subject.trim() || !messageForm.message.trim()}
                  className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
                >
                  {saving ? "Sending..." : "Send message"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowMessageModal(false)}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
          </div>
        </Portal>
      )}

      <SponsorMatchModal
        open={showGlobalMatchModal}
        onClose={() => setShowGlobalMatchModal(false)}
        sponsorId={sponsor.id}
        onMatched={() => {
          void reloadPageData()
        }}
      />

      {/* Legacy Match Child Modal */}
      {showMatchModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Match a Child</h2>
            <p className="mt-1 text-sm text-ink/60">
              {matchStep === 1 && "Step 1 of 3 · Select a child"}
              {matchStep === 2 && "Step 2 of 3 · Review selection"}
              {matchStep === 3 && "Step 3 of 3 · Confirm match"}
            </p>

            {matchStep === 1 && (
              <div className="mt-6 space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <div className="relative flex-1">
                    <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
                    <input
                      type="text"
                      placeholder="Search by name..."
                      value={matchSearch}
                      onChange={(e) => setMatchSearch(e.target.value)}
                      className="h-10 w-full rounded-lg border border-gold-500 bg-cream-50 pl-10 pr-4 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                    />
                  </div>
                  <Select
                    value={matchState}
                    onChange={setMatchState}
                    options={SPONSOR_FILTER_STATES}
                  />
                  <Select
                    value={matchGender}
                    onChange={setMatchGender}
                    options={["All", "Male", "Female"]}
                  />
                </div>

                {loadingChildren ? (
                  <div className="rounded-lg border border-gold-500/30 p-8 text-center">
                    <p className="text-sm text-ink/50">Loading available children...</p>
                  </div>
                ) : availableChildren.length === 0 ? (
                  <div className="rounded-lg border border-gold-500/30 p-8 text-center">
                    <p className="text-sm text-ink/50">No children available for matching</p>
                  </div>
                ) : (
                  <div className="max-h-[300px] space-y-2 overflow-y-auto">
                    {availableChildren.map((child) => (
                      <div
                        key={child.id}
                        onClick={() => setSelectedChild(child)}
                        className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors ${
                          selectedChild?.id === child.id
                            ? "border-forest-700 bg-forest-50"
                            : "border-gold-500/30 hover:bg-cream-50"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {child.photoUrl ? (
                            <img
                              src={child.photoUrl}
                              alt={`${child.firstName} ${child.lastName}`}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest-950 text-xs font-bold text-white">
                              {child.firstName[0]}{child.lastName[0]}
                            </span>
                          )}
                          <div>
                            <p className="text-sm font-medium text-ink">{child.firstName} {child.lastName}</p>
                            <p className="text-xs text-ink/50">
                              Age {child.age} · {child.gender === "male" ? "Male" : "Female"} · {child.state}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-ink/50">{child.code}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMatchModal(false)
                      setSelectedChild(null)
                    }}
                    className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setMatchStep(2)}
                    disabled={!selectedChild}
                    className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {matchStep === 2 && selectedChild && (
              <div className="mt-6 space-y-4">
                <div className="rounded-lg border border-gold-500 bg-cream-50 p-4">
                  <p className="text-xs uppercase tracking-wider text-ink/50">Selected Child</p>
                  <div className="mt-3 flex items-center gap-4">
                    {selectedChild.photoUrl ? (
                      <img
                        src={selectedChild.photoUrl}
                        alt={`${selectedChild.firstName} ${selectedChild.lastName}`}
                        className="h-16 w-16 rounded-full object-cover"
                      />
                    ) : (
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-forest-950 text-lg font-bold text-white">
                        {selectedChild.firstName[0]}{selectedChild.lastName[0]}
                      </span>
                    )}
                    <div>
                      <p className="text-lg font-medium text-ink">{selectedChild.firstName} {selectedChild.lastName}</p>
                      <p className="text-sm text-ink/60">
                        {selectedChild.code} · Age {selectedChild.age} · {selectedChild.gender === "male" ? "Male" : "Female"} · {selectedChild.state}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gold-500 bg-cream-50 p-4">
                  <p className="text-xs uppercase tracking-wider text-ink/50">Will be matched with</p>
                  <div className="mt-3 flex items-center gap-4">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-forest-950 text-lg font-bold text-white">
                      {sponsor.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                    </span>
                    <div>
                      <p className="text-lg font-medium text-ink">{sponsor.name}</p>
                      <p className="text-sm text-ink/60">
                        {sponsor.code} · {TIER_LABELS[sponsor.tier]} · Since {sponsor.since}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setMatchStep(1)}
                    className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setMatchStep(3)}
                    className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {matchStep === 3 && selectedChild && (
              <div className="mt-6 space-y-4">
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                  <p className="text-sm text-green-700">
                    You are about to match <strong>{selectedChild.firstName} {selectedChild.lastName}</strong> with <strong>{sponsor.name}</strong>.
                  </p>
                  <p className="mt-2 text-xs text-green-600">
                    This will mark the child as sponsored and associate them with this sponsor.
                  </p>
                </div>

                <div className="flex justify-between gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setMatchStep(2)}
                    className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleMatchChild}
                    disabled={saving}
                    className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
                  >
                    {saving ? "Matching..." : "Confirm Match"}
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
        </Portal>
      )}

      {/* Unmatch Modal */}
      {showUnmatchModal && childToUnmatch && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 p-4 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
            <div
              ref={modalRef}
              className="w-full max-w-md rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
            >
              <h2 className="font-display text-xl font-bold text-ink">End student match</h2>
              <p className="mt-2 text-sm leading-6 text-ink/65">
                This will make {childToUnmatch.firstName} {childToUnmatch.lastName} available for a new sponsor. The match history will be kept.
              </p>
              <label className="mt-5 block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Reason</span>
                <textarea
                  value={unmatchReason}
                  onChange={(event) => setUnmatchReason(event.target.value)}
                  rows={4}
                  placeholder="Why is this match ending?"
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </label>
              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowUnmatchModal(false)
                    setChildToUnmatch(null)
                    setUnmatchReason("")
                  }}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleUnmatchChild()}
                  disabled={saving || !unmatchReason.trim()}
                  className="rounded-full bg-red-700 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-800 disabled:opacity-50"
                >
                  {saving ? "Ending match..." : "End match"}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Pause Modal */}
      {showPauseModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Pause Sponsor</h2>
            <p className="mt-1 text-sm text-ink/60">{sponsor.code} · {sponsor.name}</p>
            <div className="mt-6 space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Reason (optional)</span>
                <textarea
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  rows={3}
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Why is this sponsor being paused?"
                />
              </label>

              <div className="rounded-lg border border-gold-500 bg-gold-50 p-3">
                <p className="text-sm text-gold-700">
                  Pausing a sponsor will change their status but keep their sponsorship records intact.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPauseModal(false)
                    setPauseReason("")
                  }}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePause}
                  disabled={saving}
                  className="rounded-full bg-gold-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gold-700 disabled:opacity-50"
                >
                  {saving ? "Pausing..." : "Pause Sponsor"}
                </button>
              </div>
            </div>
          </div>
          </div>
        </Portal>
      )}
    </div>
  )
}
